/**
 * @description 系统日志记录工具
 * @date 2025-12-17
 */
import axios from 'axios'
import store from '@/store'

// 系统类型
const SYSTEM_TYPE = 5

// 接口域名
const apiDomain = process.env.VUE_APP_LOG_DOMAIN
// 接口URL
const LOG_API_URL = '/datacenter_pms/api/pms/systemLog/serverLog/save/v100'
// 接口authentication
const AUTHORIZATION = process.env.VUE_APP_LOG_AUTHORIZATION

// 不需要记录的页面名称
const excludeRouteNames = new Set(['log', 'refresh', 'redirect', 'Preview', 'PreviewNew', '404'])

// 供应商ID为0时，设置为-1
const DEFAULT_PROVIDER_ID = -1
// 全局信息缓存
let globalInfo = null
let inited = false

// 待处理的日志队列
const logQueue = []

// 日志发送控制
let isProcessingQueue = false // 是否正在处理队列
const BATCH_SIZE = 5 // 每批处理的日志数量
const BATCH_DELAY = 100 // 每批之间的延迟（毫秒）

/**
 * @description 打印错误日志
 * @param {Error|*} error 错误对象
 * @param {string} prefix 错误前缀，默认为 '[Request]记录系统日志失败'
 * @param {string} color 错误颜色，默认为 '#f04949'（红色）
 */
function logError(error, prefix = '[Request]记录系统日志失败', color = '#f04949') {
  console.log('%c%s%s', `font-style: italic; color: ${color};`, prefix, error)
}

/**
 * @description 判断是否需要记录系统日志
 * @param {*} route
 * @returns
 */
function isValidRoute(route) {
  if (!route) return false
  return !excludeRouteNames.has(route.name)
}

/**
 * @description 提取路由标题列表
 * @param {*} route
 * @returns {string[]}
 */
function extractRouteTitles(route) {
  const titleList = []
  if (!route) return titleList
  const matched = route.matched || []
  matched.forEach((r) => {
    if (r.meta?.title) {
      titleList.push(r.meta.title)
    }
  })
  return titleList
}

/**
 * @description 构建日志参数
 * @param {string} optMenu 操作菜单
 * @returns {object}
 */
function buildLogParams(optMenu) {
  return {
    ...globalInfo,
    optMenu
  }
}

/**
 * @description 发送日志（统一错误处理）
 * @param {object} params 日志参数
 */
function sendLog(params) {
  return saveSystemLog(params).catch((err) => logError(err))
}

/**
 * @description 处理队列中的日志（带频率控制）
 */
async function processLogQueue() {
  if (isProcessingQueue || logQueue.length === 0) {
    return
  }

  isProcessingQueue = true

  try {
    while (logQueue.length > 0) {
      // 取出一批日志
      const batch = logQueue.splice(0, BATCH_SIZE)

      // 批量发送
      const promises = batch.map((logData) => {
        const params = buildLogParams(logData.optMenu)
        return sendLog(params)
      })

      // 等待当前批次完成
      await Promise.all(promises)

      // 如果还有剩余日志，延迟后再处理下一批
      if (logQueue.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY))
      }
    }
  } finally {
    isProcessingQueue = false
  }
}

/**
 * @description 设置全局信息（在用户信息加载后调用）
 * @param {object} info 用户信息
 * @property {string} name 用户名
 * @property {string} ysbProviderId 供应商ID
 */
export function setGlobalInfo(info) {
  if (!info || !info.name) return
  const { name, ysbProviderId } = info
  // 供应商ID为0时，设置为-1
  const providerId = ysbProviderId === 0 ? DEFAULT_PROVIDER_ID : ysbProviderId
  globalInfo = {
    userName: name,
    providerId,
    systemType: SYSTEM_TYPE
  }
  inited = true

  // 处理队列中的日志
  if (logQueue.length > 0) {
    processLogQueue()
  }
}

/**
 * @description 获取全局信息
 * @returns {object|null}
 * @property {string} username 用户名
 * @property {string} providerId 供应商ID
 * @property {number} systemType 系统类型
 */
function getGlobalInfo() {
  if (inited && globalInfo) {
    return globalInfo
  }
  // 如果未初始化，尝试从 store 读取（降级方案）
  const info = store.state.d2admin.user.info
  if (info && info.name) {
    setGlobalInfo(info)
    return globalInfo
  }
  return null
}

/**
 * @description 保存系统日志
 * @param {*} data
 * @returns
 */
function saveSystemLog(data) {
  return axios({
    method: 'post',
    url: `${apiDomain}${LOG_API_URL}`,
    data,
    headers: {
      AUTHORIZATION: AUTHORIZATION
    }
  })
}
/**
 * @description 记录页面访问日志
 * @param {*} route
 * @returns
 */
export async function writeSystemLog(route) {
  try {
    if (!isValidRoute(route)) return

    const titleList = extractRouteTitles(route)
    if (!titleList.length) return

    const optMenu = titleList.join('-')
    const currentGlobalInfo = getGlobalInfo()

    // 如果全局信息未初始化，将日志加入队列
    if (!currentGlobalInfo) {
      logQueue.push({ optMenu })
      return
    }

    // 直接发送日志
    sendLog(buildLogParams(optMenu))
  } catch (error) {
    logError(error, '记录系统日志失败', '#49f6f0')
  }
}
